/**
 * Fixed-timestep simulation + free-running render, driven by rAF.
 *
 * Rationale (see DESIGN.md -> "Game Loop"):
 *  - Hit detection and the AI's reaction-delay queue are timing-critical. If the
 *    simulation stepped at a variable rate they would behave differently on a
 *    60 Hz laptop and a 144 Hz desktop, which is unacceptable when the whole
 *    difficulty curve is expressed in milliseconds.
 *  - Rendering is decoupled and interpolates, so a 144 Hz display still looks
 *    smooth while the sim runs at a stable 120 Hz.
 *  - Pose inference is *not* in this loop. It runs at whatever rate the model
 *    manages (typically 25-40 Hz) and pushes into the sim asynchronously.
 */
export interface LoopHooks {
  /** Fixed-rate simulation step. `dt` is always `stepMs`. */
  update(dtMs: number, nowMs: number): void;
  /** Called once per animation frame. `alpha` is the fraction into the next step. */
  render(alpha: number, nowMs: number): void;
}

export class GameLoop {
  private raf = 0;
  private acc = 0;
  private last = 0;
  private running = false;

  /** Rolling stats for the debug overlay. */
  readonly stats = { fps: 0, simHz: 0, frameMs: 0, spiralGuards: 0 };
  private frameCount = 0;
  private stepCount = 0;
  private statWindowStart = 0;

  constructor(
    private hooks: LoopHooks,
    readonly stepMs = 1000 / 120,
    /** Never simulate more than this much wall time in one frame (spiral-of-death guard). */
    readonly maxCatchUpMs = 100,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.statWindowStart = this.last;
    this.acc = 0;
    const tick = (now: number) => {
      if (!this.running) return;
      this.raf = requestAnimationFrame(tick);
      const frameStart = now;

      let elapsed = now - this.last;
      this.last = now;
      if (elapsed > this.maxCatchUpMs) {
        elapsed = this.maxCatchUpMs;
        this.stats.spiralGuards++;
      }
      this.acc += elapsed;

      while (this.acc >= this.stepMs) {
        this.hooks.update(this.stepMs, now - this.acc + this.stepMs);
        this.acc -= this.stepMs;
        this.stepCount++;
      }

      this.hooks.render(this.acc / this.stepMs, now);
      this.frameCount++;

      const win = now - this.statWindowStart;
      if (win >= 500) {
        this.stats.fps = (this.frameCount * 1000) / win;
        this.stats.simHz = (this.stepCount * 1000) / win;
        this.frameCount = 0;
        this.stepCount = 0;
        this.statWindowStart = now;
      }
      this.stats.frameMs = this.stats.frameMs * 0.9 + (performance.now() - frameStart) * 0.1;
    };
    this.raf = requestAnimationFrame(tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
  }

  get isRunning(): boolean {
    return this.running;
  }
}
